import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import MuiBox from '@material-ui/core/Box';
import MuiButton from '@material-ui/core/Button';
import MuiTypography from '@material-ui/core/Typography';
import MuiDivider from '@material-ui/core/Divider';
import ClearIcon from '@material-ui/icons/Clear';
import GetAppIcon from '@material-ui/icons/GetApp';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import ConfirmationDialog from '../ConfirmationDialog';

const styles = theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  selectedText: {
    flexGrow: 1,
    paddingLeft: theme.spacing(1),
  },
  actionButton: {
    marginLeft: theme.spacing(1),
    color: theme.palette.primary.contrastText,
    borderColor: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  divider: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
});

/**
 * Bulk actions component for selected items
 */
const BulkActions = ({
  classes,
  selectedCount,
  onClearSelection,
  onExport,
  onSetToPending,
  loading = false,
}) => {
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    action: null,
  });

  const handleExport = () => {
    setConfirmDialog({
      open: true,
      title: 'Export Selected Files',
      content: `Are you sure you want to export ${selectedCount} selected file${
        selectedCount !== 1 ? 's' : ''
      }?`,
      action: () => {
        onExport();
        setConfirmDialog({ open: false, title: '', content: '', action: null });
      },
    });
  };

  const handleSetToPending = () => {
    setConfirmDialog({
      open: true,
      title: 'Set Files to Pending',
      content: `Are you sure you want to set ${selectedCount} selected file${
        selectedCount !== 1 ? 's' : ''
      } to pending status? This action cannot be undone.`,
      action: () => {
        onSetToPending();
        setConfirmDialog({ open: false, title: '', content: '', action: null });
      },
    });
  };

  const handleCloseDialog = () => {
    setConfirmDialog({ open: false, title: '', content: '', action: null });
  };

  if (selectedCount === 0) return null;

  return (
    <>
      <MuiBox className={classes.root}>
        <MuiTypography variant="body2" className={classes.selectedText}>
          {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected
        </MuiTypography>

        <MuiButton
          size="small"
          onClick={onClearSelection}
          className={classes.actionButton}
          startIcon={<ClearIcon />}
          disabled={loading}>
          Clear
        </MuiButton>

        <MuiDivider orientation="vertical" className={classes.divider} />

        <MuiButton
          size="small"
          onClick={handleExport}
          className={classes.actionButton}
          startIcon={<GetAppIcon />}
          disabled={loading}>
          Export
        </MuiButton>

        <MuiButton
          size="small"
          onClick={handleSetToPending}
          className={classes.actionButton}
          startIcon={<PlayArrowIcon />}
          disabled={loading}>
          Set to Pending
        </MuiButton>
      </MuiBox>

      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={handleCloseDialog}
        onConfirm={confirmDialog.action}
        title={confirmDialog.title}
        content={confirmDialog.content}
        confirmText="Confirm"
        cancelText="Cancel"
        loading={loading}
      />
    </>
  );
};

BulkActions.propTypes = {
  classes: PropTypes.object.isRequired,
  selectedCount: PropTypes.number.isRequired,
  onClearSelection: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onSetToPending: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default withStyles(styles)(BulkActions);
